import { z } from "zod";

/** One user's guided-tour progress, stored per vendor in `VendorProperties.tourCompletions`. */
export const tourCompletionSchema = z
  .object({
    userEmail: z.email(),
    /** Main-tour registry version last completed or skipped; absent until the user has done so */
    version: z.number().int().positive().optional(),
    /** Registry version the feature (what's-new) tour was last seen or skipped at; the full tour counts too. Absent until then */
    featureTourVersion: z.number().int().positive().optional(),
    /** Highest step count reached in the user's most recent full-tour run */
    stepsCompleted: z.number().int().nonnegative().optional(),
    /** True once the onboarding setup guide was skipped or its map guide finished */
    onboardingTourCompleted: z.boolean().optional(),
  })
  .refine(
    (entry) =>
      entry.version !== undefined ||
      entry.featureTourVersion !== undefined ||
      entry.stepsCompleted !== undefined ||
      entry.onboardingTourCompleted === true,
    { message: "A completion entry must record a version, featureTourVersion, stepsCompleted or onboardingTourCompleted: true" }
  );

export type TourCompletion = z.infer<typeof tourCompletionSchema>;

/** An entry minus its identity: what the server merges and the client sends as changes */
export type TourCompletionUpdate = Omit<TourCompletion, "userEmail">;

/** Request shape for the vendor-preferences write: exactly the caller's own entry */
export const tourCompletionRequestSchema = z.tuple([tourCompletionSchema]);

/** Emails are compared and stored in this form so lookups are case-insensitive */
export const normalizeTourEmail = (email: string): string => email.trim().toLowerCase();

export const findTourCompletion = (entries: readonly TourCompletion[], email: string): TourCompletion | undefined => {
  const normalizedEmail = normalizeTourEmail(email);
  return entries.find((entry) => normalizeTourEmail(entry.userEmail) === normalizedEmail);
};

/** Newer version replaces stepsCompleted; same version keeps the higher count; older/missing writes keep the existing value. */
const mergeStepsCompleted = (existing: TourCompletion | undefined, incoming: TourCompletionUpdate): number | undefined => {
  if (incoming.stepsCompleted === undefined) return existing?.stepsCompleted;
  const existingVersion = existing?.version ?? 0;
  const incomingVersion = incoming.version ?? 0;
  if (existing === undefined || incomingVersion > existingVersion) return incoming.stepsCompleted;
  if (incomingVersion === existingVersion) return Math.max(existing.stepsCompleted ?? 0, incoming.stepsCompleted);
  return existing.stepsCompleted;
};

/** Highest value either side has; undefined while neither has one */
const mergeMax = (existing: number | undefined, incoming: number | undefined): number | undefined => {
  if (existing === undefined) return incoming;
  if (incoming === undefined) return existing;
  return Math.max(existing, incoming);
};

/** Merges a completion write into the per-user list; versions never downgrade and the onboarding flag never clears. */
export const upsertTourCompletion = (
  entries: readonly TourCompletion[],
  email: string,
  incoming: TourCompletionUpdate
): TourCompletion[] => {
  const normalizedEmail = normalizeTourEmail(email);
  const existing = findTourCompletion(entries, normalizedEmail);
  const otherEntries = entries.filter((entry) => normalizeTourEmail(entry.userEmail) !== normalizedEmail);

  const version = mergeMax(existing?.version, incoming.version);
  const featureTourVersion = mergeMax(existing?.featureTourVersion, incoming.featureTourVersion);
  const stepsCompleted = mergeStepsCompleted(existing, incoming);
  const onboardingTourCompleted = existing?.onboardingTourCompleted === true || incoming.onboardingTourCompleted === true;

  // `existing` first, so fields this code does not know about survive the write
  const merged: TourCompletion = {
    ...existing,
    userEmail: normalizedEmail,
    ...(version === undefined ? {} : { version }),
    ...(featureTourVersion === undefined ? {} : { featureTourVersion }),
    ...(stepsCompleted === undefined ? {} : { stepsCompleted }),
    ...(onboardingTourCompleted ? { onboardingTourCompleted } : {}),
  };
  return [...otherEntries, merged];
};
